import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Icon,
  // , palette
} from '@m12s/component-library';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import routes from 'lib/utils/routes';
import moment from 'moment';
import { fromNow } from 'lib/utils/date';
import { useTranslation } from 'react-i18next';

import { getMachine } from 'lib/selectors/getMachine';
import {
  getCurrentWorkOrderOp,
  getCurrentWorkOrderOpStartAt,
  // getCurrentWorkOrderOpCompletionDelta,
  // getCurrentWorkOrderOpCompletionDeltaText,
  getCurrentWorkOrderOpTimeToComplete,
} from 'lib/selectors/getCurrentWorkOrderOp';

import { getIsJobLaborTrackingEnabled } from 'lib/selectors/getIsJobLaborTrackingEnabled';
import { getOrderProgress } from 'lib/selectors/getOrderProgress';
import { useJobDisplayName } from 'lib/hooks/useJobDisplayName';
import {
  OpButton,
  TopCard,
  BottomCardContainer,
  InfoGroup,
  Divider,
} from './styled';
import { withNotApplicableFallback, withDashFallback } from './helpers';
import PartCountingCard from './PartCountingCard';

const formatDate = (timestamp) => {
  const momentTimestamp = moment(timestamp);

  if (momentTimestamp.isSame(moment(), 'day')) {
    return `Today, ${momentTimestamp.format('LT')}`;
  }
  return momentTimestamp.format('lll');
};

const Top = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const orderProgress = useSelector(getOrderProgress);

  // const completionDelta = useSelector(getCurrentWorkOrderOpCompletionDelta);
  // const completionDeltaText = useSelector(
  //   getCurrentWorkOrderOpCompletionDeltaText
  // );
  const timeToComplete = useSelector(getCurrentWorkOrderOpTimeToComplete);
  const isJobLaborTrackingEnabled = useSelector(getIsJobLaborTrackingEnabled);

  const { finishQuantity, workOrder } = currentWorkOrderOp || {};

  // Use real-time parts count (includes both work order and labor scope parts)
  const partsRemaining = Math.max(finishQuantity - orderProgress, 0);
  // const isEarly = completionDelta.asSeconds() <= 0;

  return (
    <>
      <TopCard
        label="Production Order Operation"
        value={workOrder?.workOrderId || 'None'}
        subValue={
          isJobLaborTrackingEnabled ? (
            <></>
          ) : (
            <OpButton
              onClick={() => {
                return history.push(
                  `${routes.machineIdSelectJobPath(
                    machine.id
                  )}?updateWorkOrderId=true`
                );
              }}
            >
              <Icon
                icon={faEdit}
                size="0.75rem"
                style={{ marginRight: '0.375rem' }}
              />
              Change Operation
            </OpButton>
          )
        }
      />
      <TopCard
        label="Parts Remaining"
        value={Math.max(partsRemaining || 0, 0)?.toLocaleString()}
        subValue={`${orderProgress?.toLocaleString() ||
          '-'} / ${finishQuantity?.toLocaleString()}`}
      />
      <TopCard
        label="Est. Completion Date"
        value={
          orderProgress >= finishQuantity
            ? t('Complete')
            : timeToComplete === null
            ? '-'
            : formatDate(fromNow(timeToComplete))
        }
        // TODO larger design / logic discussion needed of if this delta makes sense + currently do not have access to ETTC
        // subValue={
        //   <span
        //     style={{
        //       color: isEarly ? palette.Green600 : palette.Red600,
        //       fontSize: '0.75rem',
        //       fontWeight: '400',
        //     }}
        //   >
        //     {completionDeltaText}
        //   </span>
        // }
      />
      <PartCountingCard />
    </>
  );
};

const Bottom = () => {
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const timeToComplete = useSelector(getCurrentWorkOrderOpTimeToComplete);
  const startAt = useSelector(getCurrentWorkOrderOpStartAt);
  const { getOrderOpDisplayName } = useJobDisplayName();

  // Memoize the order op display name to avoid re-rendering the component when the work order op changes
  const orderOpDisplayName = useMemo(() => {
    return getOrderOpDisplayName(currentWorkOrderOp);
  }, [getOrderOpDisplayName, currentWorkOrderOp]);

  const { scheduledStartDate, workOrder } = currentWorkOrderOp || {};

  return (
    <BottomCardContainer>
      <InfoGroup
        top={{
          label: 'Part Operation',
          value: withNotApplicableFallback(orderOpDisplayName || null),
        }}
        bottom={[
          {
            label: 'Lot',
            value: withDashFallback(workOrder.lot),
          },
          {
            label: 'Split',
            value: withDashFallback(workOrder.split),
          },
          {
            label: 'Sub',
            value: withDashFallback(workOrder.sub),
          },
        ]}
      />
      <Divider />
      <InfoGroup
        top={{
          label: 'PO Due Date',
          value: formatDate(workOrder.dueDate),
        }}
        bottom={{
          label: 'Estimated Time Remaining',
          value: `${
            timeToComplete === null
              ? 'Requires exp. cycle time'
              : moment.duration(timeToComplete).format('h[h] m[m]', {
                  largest: 2,
                }) || '0m'
          }`,
        }}
      />
      <Divider />
      <InfoGroup
        top={{
          label: 'Planned Start Date',
          value: formatDate(scheduledStartDate),
        }}
        bottom={{
          label: 'Actual Start Date',
          value: formatDate(startAt),
        }}
      />
    </BottomCardContainer>
  );
};

export default () => {
  return (
    <>
      <Top />
      <Bottom />
    </>
  );
};
